import React from "react";
import {
  StyledForm,
  ButtonGroup,
  BackButton,
  SubmitButton,
  FormLabel,
} from "./styles";
import { navigate } from "@reach/router";
import { Formik } from "formik";
import * as XLSX from "xlsx";
import { CustomFileUpload } from "../../../common/FormInputs";
import { CustomInputField } from "../../../common/FormInputs";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AWSService from "../../../../services/AWSService";
import CaseService from "../../../../services/CaseService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import { mergeUploadValidationSchema } from "../../../../helpers/validationSchema";
import {
  Card,
  FormGrid,
  Label,
  PageWrapper,
  Section,
  StyledAutocomplete,
} from "./SplitAndUpload";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import { Popper } from "@material-ui/core";
import CustomSelect from "../../NoticeGeneration/component/CustomSelect";

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const MergeAndUpload = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const upload_templates = [
    {
      name: "zipFile1",
      label: "Zip File One",
      type: "upload",
      required: true,
      accept: [".zip"],
      types: ["application/zip"],
    },
    {
      name: "zipFile2",
      label: "Zip File Two",
      type: "upload",
      required: true,
      accept: [".zip"],
      types: ["application/zip"],
    },
  ];

  /*

  async function s3Upload(
    file,
    field,
    setFieldValue,
    fileTypes,
    accept,
    values,
    fileName,
  ) {
    if (file) {
      try {
        const types = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
          "application/zip",
        ];
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const errorMessage = "Invalid File Type";
        const acceptList = accept[0].split(",").map((x) => x.trim());
        if (!types.includes(file?.type)) throw errorMessage;
        if (accept === ".xlsx") {
          const response = await readExcelFile(file, fileName, setFieldValue);
          setFieldValue(field, response);
          if (response?.length) {
            setFieldValue(fileName, file.name);
          }
        } else if (acceptList.includes(".zip")) {
          const payload = {
            key: file.name,
          };
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
            enqueueSnackbar("Successfully Uploaded", { variant: "success" });
          }
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  */

  async function s3Upload(
    file,
    field,
    setFieldValue,
    fileTypes,
    accept,
    values,
    fileName,
  ) {
    if (file) {
      try {
        setLoader({ state: true, message: `Uploading file ${file.name}` });

        const acceptList = accept[0]
          ? accept[0].split(",").map((x) => x.trim())
          : [accept];

        const excelTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
        ];

        const zipMimeTypes = [
          "application/zip",
          "application/x-zip-compressed",
          "application/x-zip",
          "application/octet-stream",
          "multipart/x-zip",
          "",
        ];

        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (acceptList.includes(".zip")) {
          if (fileExtension !== "zip") {
            throw new Error("Invalid File Type. Please upload a .zip file.");
          }
        } else if (acceptList.includes(".xlsx")) {
          if (!excelTypes.includes(file?.type)) {
            throw new Error("Invalid File Type. Please upload a .xlsx file.");
          }
        } else {
          const allTypes = [...excelTypes, ...zipMimeTypes];
          if (!allTypes.includes(file?.type)) {
            throw new Error("Invalid File Type");
          }
        }

        if (acceptList.includes(".xlsx")) {
          const response = await readExcelFile(file, fileName, setFieldValue);
          setFieldValue(field, response);
          if (response?.length) {
            setFieldValue(fileName, file.name);
          }
        } else if (acceptList.includes(".zip")) {
          const payload = { key: file.name };
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
            enqueueSnackbar("Successfully Uploaded", { variant: "success" });
          }
        }
      } catch (err) {
        enqueueSnackbar(err?.message || "Upload failed", {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  /**
   * @description Read xlsx file
   * @param {*} file
   * @returns
   */

  const readExcelFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer(); // Convert file to ArrayBuffer
    const workbook = XLSX.read(arrayBuffer, { type: "array" }); // Read the workbook
    const sheetName = workbook.SheetNames[0]; // Get the first sheet
    const sheet = workbook.Sheets[sheetName]; // Access the first sheet
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Convert to 2D array

    const headerRow = data[0]; // First row is the header
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
    ];
    const contractNumberIndex = headerRow.findIndex((header) =>
      possibleHeaders.includes(header.trim()),
    );

    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", {
        variant: "error",
      });
    }

    // // Extract values under "CONTRACT NUMBER" column
    const contractNumbers = data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);

    return contractNumbers;
  };

  /**
   * @description onFormSubmit
   */

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: `merge and upload...` });
      const payload = {
        partyId: values.partyId,
        zipFile1: values.zipFile1,
        zipFile2: values?.zipFile2,
        contractNo: values.contractNo.map(String),
        noticeCategory: values.noticeCategory,
      };
      const response = await CaseService.mergeUpload(payload);
      if (response?.message) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
      resetForm();
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            excel: "",
            zipFile1: "",
            zipFile2: "",
            contractNo: "",
            noticeCategory: "",
            partyId: "",
          }}
          validationSchema={mergeUploadValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldError,
            resetForm,
          }) => (
            <StyledForm>
              <Section>
                <FormGrid>
                  <CustomSelect
                    name="partyId"
                    trigger={true}
                    getNoticeTemplates={() => {}}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    menuItems={menuItems}
                  />
                </FormGrid>
              </Section>
              <Section>
                <FormGrid>
                  <div>
                    <Label>Pre Name</Label>
                    <StyledAutocomplete
                      id="notice-category-autocomplete"
                      size="small"
                      options={menuItems}
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            placeholder="Select notice category..."
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                            }}
                          />
                        </div>
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>
                          <div
                            style={{
                              padding: "4px 0",
                              color: COLORS.COLOR_DARK,
                              fontFamily: theme.fonts.primaryFontSemiBold,
                            }}
                          >
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: COLORS.INPUT_LABEL,
                            fontFamily: "openSans-SemiBold, sans-serif",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setFieldValue("noticeCategory", newValue?.name || "")
                      }
                    />
                  </div>

                  <div>
                    {[
                      {
                        name: "contractNo",
                        label: "Upload Xlsx",
                        type: "upload",
                        required: true,
                        fileName: "excel",
                        accept: [".xlsx"],
                        types: [
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                          "application/vnd.ms-excel",
                          "text/csv",
                        ],
                      },
                    ].map((upload, index) => (
                      <div key={upload.name}>
                        <FormLabel>
                          {upload.label}{" "}
                          <span className="required-star"> *</span>
                        </FormLabel>
                        <CustomFileUpload
                          error={
                            touched[upload.fileName] && errors[upload.fileName]
                          }
                          setFile={(file) =>
                            s3Upload(
                              file,
                              upload.name,
                              setFieldValue,
                              upload.types,
                              upload.accept,
                              values,
                              upload.fileName,
                            )
                          }
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                          }}
                          accept={upload.accept}
                          file={{
                            name: values[upload.fileName],
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </FormGrid>
              </Section>
              <Section>
                <FormGrid className="mis-dashboard">
                  {upload_templates.map((upload, index) => (
                    <div key={upload.name}>
                      <FormLabel>
                        {upload.label} <span className="required-star"> *</span>
                      </FormLabel>
                      <CustomFileUpload
                        error={touched[upload.name] && errors[upload.name]}
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            upload.types,
                            upload.accept,
                            values,
                            upload.fileName,
                          )
                        }
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                        accept={upload.accept}
                        file={{
                          name: getFileName(values[upload.name]),
                        }}
                      />
                    </div>
                  ))}
                </FormGrid>
              </Section>
              <ButtonGroup>
                <BackButton onClick={() => navigate("/dashboard/case-manager")}>
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

export default MergeAndUpload;
